import { useTranslation } from 'react-i18next';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Button, ButtonBase } from '@mui/material';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useReducedMotion } from 'framer-motion';

import { AtmosphericGlow, useFeatureFlags } from '@nexus/supabase';

import {
  AccountMenu,
  AccountMenuItem,
  accountMenuIconSx,
  DEFAULT_LEGAL_LINKS,
} from '@nexus/ari-lib/components/account-menu';
import {
  resolveAvatarSrc,
  useProfilePicture,
  createMicrosoftPhotoResolver,
} from '@nexus/ari-lib/components/account-avatar';
import { zIndex } from '@nexus/ari-lib/theme/theme-config';
import { gradientBorderSx } from '@nexus/ari-lib/theme/surfaces';
import { RouteTransition } from '@nexus/ari-lib/components/motion/route-transition';
import { TopNavLayout, topNavRoundButtonSx } from '@nexus/ari-lib/components/top-nav-layout';
import { Mic, Building2 } from 'lucide-react';

import ariCloudLogo from '../assets/ari-cloud-logo.svg';

import { useStore } from '../lib/store';
import { useAuth } from '../hooks/use-auth';
import { fetchGraphToken } from '../lib/fetch-graph-token';
import { getAriUrl, getSubscriptionManagerUrl, isDev } from '../config/global';
import { useAriBusinessUrl } from '../hooks/use-ari-business-url';
import { useWorkspaces } from '../hooks/use-workspaces';
import { canUpgradeIn } from '../sections/upgrade/plan-data';
import { ARI_BUSINESS_BILLING_PATH } from '../lib/ari-business-url';

// Module scope: fetchGraphToken is a stable singleton, so the resolver is built once.
const resolveMicrosoftPhoto = createMicrosoftPhotoResolver(fetchGraphToken);

// ----------------------------------------------------------------------

type TabItem = {
  label: string;
  path: string;
  badge?: string;
};

// Built per render so the dev-only Advanced tab can be appended via conditional
// spread. Labels stay hardcoded to match the other tabs (no i18n).
export function buildTabItems(): TabItem[] {
  return [
    { label: 'Devices', path: '/devices' },
    { label: 'Connectors', path: '/connectors' },
    { label: 'Skills', path: '/skills' },
    { label: 'Memory', path: '/memory' },
    { label: 'Chats', path: '/chats' },
    { label: 'Media', path: '/media' },
    { label: 'Documents', path: '/documents' },
    ...(isDev() ? [{ label: 'Advanced', path: '/advanced' }] : []),
  ];
}

// ----------------------------------------------------------------------

interface CloudLayoutProps {
  children: React.ReactNode;
}

export function CloudLayout({ children }: CloudLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const supabaseUser = useStore((s) => s.supabaseUser);
  const pictureSrc = useProfilePicture(supabaseUser, resolveMicrosoftPhoto);
  const isBusiness = useStore((s) => s.accountType) === 'business';
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const ariBusinessUrl = useAriBusinessUrl();
  const { crtWorkspaceData, error: workspacesError, isLoading: workspacesLoading } = useWorkspaces();
  // Never while the workspace is unresolved.
  const canUpgrade = !workspacesError && !workspacesLoading && canUpgradeIn(crtWorkspaceData);
  // this is how we get the feature flags values managed inside internal hub
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { flags: featureFlags } = useFeatureFlags(getSubscriptionManagerUrl());

  // The skill gallery is a step inside Skills: its own back button replaces the tabs.
  const hideTabs = location.pathname.startsWith('/skills/browse');
  const tabs = buildTabItems().map((item) => ({
    ...item,
    'data-testid': `cloud.nav.${item.path.replace('/', '')}.tab`,
  }));

  return (
    <TopNavLayout
      data-testid="cloud.layout.container"
      // Same blue-toned glow as the auth screen, rising from below the viewport.
      backdrop={<AtmosphericGlow zIndex={zIndex.base} />}
      logo={
        // Plain img keeps the width and height attributes for the aspect ratio.
        <img
          data-testid="cloud.layout.logo"
          src={ariCloudLogo}
          alt="Ari Cloud"
          width={128}
          height={23}
          style={{ height: 23, width: 'auto', filter: 'brightness(0) invert(1)' }}
        />
      }
      actions={
        <>
          {isBusiness ? (
            // Business accounts manage their plan on the Ari Business portal, so
            // the gradient "Upgrade plan" CTA is replaced by an organization link.
            <Button
              data-testid="cloud.layout.business-btn"
              onClick={() => window.open(ariBusinessUrl(), '_blank', 'noopener,noreferrer')}
              startIcon={<Building2 size={16} />}
              sx={{
                height: 36,
                px: 1,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1,
                minHeight: 0,
                textTransform: 'none',
                color: 'common.white',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'transparent', opacity: 0.75 },
              }}
            >
              RealWear
            </Button>
          ) : (
            canUpgrade && (
              <Button
                data-testid="cloud.layout.upgrade-btn"
                component="a"
                href={ariBusinessUrl(ARI_BUSINESS_BILLING_PATH)}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color="primary"
                sx={(theme) => ({
                  height: { xs: 30, sm: 36 },
                  px: { xs: 1.25, sm: 2 },
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 600,
                  lineHeight: 1,
                  minHeight: 0,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...gradientBorderSx(theme),
                })}
              >
                Upgrade plan
              </Button>
            )
          )}

          {/* Ari app button */}
          <ButtonBase
            aria-label={t('accessibility.openAri')}
            onClick={() => window.open(getAriUrl(), '_blank')}
            sx={(theme) => topNavRoundButtonSx(theme, reduceMotion)}
          >
            <Mic size={17} color="white" strokeWidth={1.75} />
          </ButtonBase>

          <AccountMenu
            name={supabaseUser?.name}
            email={supabaseUser?.email}
            avatarSrc={resolveAvatarSrc(pictureSrc ?? undefined)}
            triggerProps={{ 'aria-label': t('accessibility.accountMenu'), 'data-testid': 'cloud.layout.avatar' }}
            items={(close) => [
              <AccountMenuItem
                key="account"
                data-testid="cloud.account-menu.account"
                label="Account"
                icon={<PersonOutlineIcon sx={accountMenuIconSx} />}
                onClick={() => {
                  navigate({ to: '/account' });
                  close();
                }}
              />,
            ]}
            legalLabel="Legal"
            legalLinks={DEFAULT_LEGAL_LINKS}
            signOutLabel="Log Out"
            signOutProps={{ 'data-testid': 'cloud.account-menu.logout' }}
            onSignOut={signOut}
          />
        </>
      }
      tabs={tabs}
      hideTabs={hideTabs}
      activePath={location.pathname}
      onNavigate={(path) => navigate({ to: path })}
      homeLabel={t('accessibility.home')}
    >
      <RouteTransition>{children}</RouteTransition>
    </TopNavLayout>
  );
}
